import { Button, Checkbox, Search, Tag } from '@carbon/react';
import type { HoComponents } from '@pos/api-client';
import { asProblem, EmptyState, FormModal, problemMessage } from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { ListLoading } from '../common/Loading';
import { ErrorNotice } from '../common/Notices';
import { groupKey, unitKey, type AddKind, type Target } from './menuDraft';

const PAGE = 50;

const toTarget = (c: Candidate, unit?: Target['unit']): Target => ({
  id: c.id,
  code: c.code,
  name: c.name,
  isActive: c.isActive,
  unit,
  ...(c.kind === 'group'
    ? {
        members: (c.members ?? []).map((m) => ({
          id: m.itemId,
          code: m.code,
          name: m.name,
          isActive: m.isActive,
          isDefault: m.isDefault,
        })),
      }
    : {}),
});

type Candidate = HoComponents['schemas']['MenuCandidateDto'];
type Unit = Candidate['units'][number];
interface Choice {
  key: string;
  already: boolean;
  target: Target;
  unit?: Unit;
  isDefault?: boolean;
}

/** The unit a tile without one sells: the default selling unit, else the base unit. */
const defaultUnit = (units: readonly Unit[]) => units.find((u) => u.isDefaultSale) ?? units.find((u) => u.isBase);

/**
 * Picks several of the menu brand's items, deals or item groups at once, for a page or for the menu only. An item is
 * picked per unit (a menu holds it once per unit; the default unit is added as "the item's default", ADR 0030); an item
 * group whole, as one button with its variants (ADR 0052). Those already there can't be picked again.
 */
export function AddToMenuDialog({
  menuId,
  kind,
  title,
  taken,
  onAdd,
  onClose,
}: {
  menuId: string;
  kind: AddKind;
  title: string;
  /**
   * Already on the page (or on the menu), saved or not, as `unitKey(target, unit)` (a null unit is the default) or, for
   * an item group, `groupKey(group)`.
   */
  taken: ReadonlySet<string>;
  onAdd(targets: Target[]): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [size, setSize] = useState(PAGE);
  const [chosen, setChosen] = useState<Map<string, Target>>(new Map());
  const candidates = useQuery({
    queryKey: ['ho', 'menu-candidates', menuId, kind, search, size],
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/menu/menus/{id}/candidates', {
        params: { path: { id: menuId }, query: { kind, search: search || undefined, page: 1, pageSize: size } },
      });
      if (!data) throw error;
      return data;
    },
    placeholderData: keepPreviousData,
  });
  const items = candidates.data?.items ?? [];
  const total = Number(candidates.data?.total ?? 0);
  // What can be picked for a candidate: each unit of an item, or the deal itself.
  const choices = (c: Candidate): Choice[] => {
    if (kind === 'group') return [{ key: groupKey(c.id), already: taken.has(groupKey(c.id)), target: toTarget(c) }];
    const units = kind === 'item' ? c.units : [];
    if (units.length === 0) return [{ key: unitKey(c.id), already: taken.has(unitKey(c.id)), target: toTarget(c) }];
    const standard = defaultUnit(units);
    return units.map((u) => {
      const isDefault = u.uomId === standard?.uomId;
      // The default unit goes on as "the item's default" (no unit), so it follows the item if that changes.
      const uomId = isDefault ? null : u.uomId;
      return {
        key: unitKey(c.id, uomId),
        unit: u,
        isDefault,
        already: taken.has(unitKey(c.id, uomId)) || taken.has(unitKey(c.id, u.uomId)),
        target: toTarget(c, { uomId, code: u.code, name: u.name }),
      };
    });
  };
  // An item group without an active variant has nothing to sell.
  const unsellable = (c: Candidate) => kind === 'group' && !c.isActive;
  const selectable = items
    .filter((c) => !unsellable(c))
    .flatMap(choices)
    .filter((o) => !o.already);
  const toggle = (key: string, target: Target, on: boolean) =>
    setChosen((current) => {
      const next = new Map(current);
      if (on) next.set(key, target);
      else next.delete(key);
      return next;
    });

  return (
    <FormModal
      size="lg"
      title={title}
      primaryText={t('priceTable.addChosen', { count: chosen.size })}
      disabled={chosen.size === 0}
      pending={false}
      onSubmit={() => {
        onAdd([...chosen.values()]);
        onClose();
      }}
      onClose={onClose}
    >
      <div className="pos-price-picker">
        <Search
          labelText={t('admin.search')}
          placeholder={t('menuEditor.search')}
          closeButtonLabelText={t('carbon.clearSearch')}
          value={search}
          // Enter searches; it must not submit the dialog.
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          onChange={(e) => {
            setSearch(e.target.value);
            setSize(PAGE);
          }}
        />
        {candidates.error && (
          <ErrorNotice
            title={problemMessage(t, asProblem(candidates.error))}
            onRetry={() => void candidates.refetch()}
          />
        )}
        <div className="pos-price-picker__bar">
          <Checkbox
            id="menu-picker-all"
            labelText={t('priceTable.selectShown', { count: selectable.length })}
            checked={selectable.length > 0 && selectable.every((o) => chosen.has(o.key))}
            disabled={selectable.length === 0}
            onChange={(_, { checked }) => selectable.forEach((o) => toggle(o.key, o.target, checked))}
          />
          <span className="pos-price-picker__count">{t('priceTable.chosen', { count: chosen.size })}</span>
        </div>
        {candidates.isLoading && <ListLoading />}
        {items.length > 0 && (
          <ul className="pos-price-picker__list" aria-label={t(`menuEditor.kinds.${kind}` as I18nKey)}>
            {items.map((c) => {
              const options = choices(c);
              const single = options[0];
              if (single && !single.unit) {
                return (
                  <li key={c.id} className="pos-price-picker__row">
                    <Checkbox
                      id={`menu-pick-${c.id}`}
                      labelText={
                        <span className="pos-price-picker__label">
                          <span className="pos-price-picker__code">{c.code}</span>
                          <span>{c.name}</span>
                        </span>
                      }
                      checked={single.already || chosen.has(single.key)}
                      disabled={single.already || unsellable(c)}
                      onChange={(_, { checked }) => toggle(single.key, single.target, checked)}
                    />
                    {kind === 'group' && (
                      <Tag type="cool-gray" title={(c.members ?? []).map((m) => m.code).join(', ')}>
                        {t('menuEditor.variants', { count: c.members?.length ?? 0 })}
                      </Tag>
                    )}
                    {single.already && <Tag type="gray">{t('menuEditor.already')}</Tag>}
                    {!single.already && c.inMenu && <Tag type="blue">{t('menuEditor.onMenu')}</Tag>}
                    {!c.isActive && <Tag type="gray">{t('priceTable.inactive')}</Tag>}
                  </li>
                );
              }
              return (
                <li key={c.id} className="pos-price-picker__item">
                  <div className="pos-price-picker__label">
                    <span className="pos-price-picker__code">{c.code}</span>
                    <span>{c.name}</span>
                    {!c.isActive && <Tag type="gray">{t('priceTable.inactive')}</Tag>}
                  </div>
                  <ul className="pos-price-picker__units" aria-label={t('priceTable.unitsOf', { name: c.name })}>
                    {options.map((o) => (
                      <li key={o.key} className="pos-price-picker__row">
                        <Checkbox
                          id={`menu-pick-${o.key}`}
                          labelText={
                            <span className="pos-price-picker__label">
                              <span className="pos-price-picker__code">{o.unit?.code}</span>
                              <span>{o.unit?.name}</span>
                            </span>
                          }
                          checked={o.already || chosen.has(o.key)}
                          disabled={o.already}
                          onChange={(_, { checked }) => toggle(o.key, o.target, checked)}
                        />
                        {o.unit && !o.unit.isBase && (
                          <Tag type="gray">{t('priceTable.unitSize', { factor: Number(o.unit.factor) })}</Tag>
                        )}
                        {o.isDefault && <Tag type="gray">{t('menuEditor.defaultUnit')}</Tag>}
                        {o.already && <Tag type="gray">{t('menuEditor.already')}</Tag>}
                        {!o.already && o.unit?.inMenu && <Tag type="blue">{t('menuEditor.onMenu')}</Tag>}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ul>
        )}
        {!candidates.isLoading && !candidates.error && items.length === 0 && (
          <EmptyState headingLevel={3} heading={t('admin.noResultsTitle')} description={t('admin.noResultsHint')} />
        )}
        {items.length < total && (
          <Button kind="ghost" onClick={() => setSize((s) => s + PAGE)}>
            {t('priceTable.showMore', { shown: items.length, total })}
          </Button>
        )}
      </div>
    </FormModal>
  );
}
